import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import authService from '../services/AuthService';
import { getApiUrl } from '../utils/env';

interface ImportResults {
  total: number;
  imported: number;
  updated: number;
  skipped: number;
  errors: string[];
}

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

const ImportDialog: React.FC<ImportDialogProps> = ({ open, onClose, onImportComplete }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImport = async () => {
    setIsUploading(true);
    setError(null);

    try {
      const token = authService.getAccessToken();

      if (!token) {
        throw new Error('Nicht authentifiziert');
      }

      const API_BASE_URL = getApiUrl();
      
      console.log('🌱 ImportDialog API Request:');
      console.log('   API_BASE_URL (verwendet):', API_BASE_URL);
      console.log('   Import URL:', `${API_BASE_URL}/import/agrarmonitor`);
      
      const response = await fetch(`${API_BASE_URL}/import/agrarmonitor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Import fehlgeschlagen');
      }

      const result = await response.json();
      setImportResults(result.results);
      
      if (result.results.imported > 0 || result.results.updated > 0) {
        onImportComplete(); // Kontakte-Liste aktualisieren
      }
    } catch (err) {
      console.error('Import-Fehler:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Import');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setImportResults(null);
    setError(null);
    setIsUploading(false);
    onClose();
  };

  const getSuccessRate = () => {
    if (!importResults) return 0;
    const successful = importResults.imported + importResults.updated;
    return importResults.total > 0 ? (successful / importResults.total) * 100 : 0;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SyncIcon />
          Agrarmonitor API-Import
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Info-Bereich */}
          <Alert severity="info">
            <Typography variant="body2">
              Importiere alle Kontakte direkt aus der Agrarmonitor API. Die Synchronisierung erfolgt 
              basierend auf dem "bearbeitet_am" Datum. Bestehende Kontakte werden nur aktualisiert, 
              wenn sie in Agrarmonitor neuer sind.
            </Typography>
          </Alert>

          {/* Import-Button */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Typography variant="h6" gutterBottom>
                Agrarmonitor-Kontakte synchronisieren
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Lädt alle Kontakte direkt von der Agrarmonitor API und synchronisiert sie 
                mit der lokalen Datenbank.
              </Typography>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={isUploading}
                startIcon={<SyncIcon />}
                size="large"
              >
                {isUploading ? 'Synchronisierung läuft...' : 'Jetzt synchronisieren'}
              </Button>
            </Box>
          </Paper>

          {/* Ladeanzeige */}
          {isUploading && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Synchronisierung mit Agrarmonitor API läuft...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {/* Fehleranzeige */}
          {error && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          {/* Ergebnisse */}
          {importResults && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6">
                  Synchronisierung abgeschlossen
                </Typography>
              </Box>
              
              <Box display="flex" gap={2} mb={2}>
                <Chip 
                  label={`${importResults.total} Gesamt`} 
                  variant="outlined" 
                />
                <Chip 
                  label={`${importResults.imported} Neu`} 
                  color="success" 
                />
                <Chip 
                  label={`${importResults.updated} Aktualisiert`} 
                  color="primary" 
                />
                <Chip 
                  label={`${importResults.skipped} Übersprungen`} 
                  color="default" 
                />
                {importResults.errors.length > 0 && (
                  <Chip 
                    label={`${importResults.errors.length} Fehler`} 
                    color="error" 
                  />
                )}
              </Box>

              <LinearProgress 
                variant="determinate" 
                value={getSuccessRate()} 
                color="success"
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Erfolgsrate: {getSuccessRate().toFixed(1)}%
              </Typography>

              {/* Fehler-Liste */}
              {importResults.errors.length > 0 && (
                <Box mt={2}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Fehler beim Import:
                  </Typography>
                  <List dense>
                    {importResults.errors.slice(0, 10).map((errorMsg, index) => (
                      <ListItem key={index}>
                        <ListItemText 
                          primary={errorMsg}
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              fontSize: '0.875rem',
                              color: 'error.main'
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                    {importResults.errors.length > 10 && (
                      <ListItem>
                        <ListItemText 
                          primary={`... und ${importResults.errors.length - 10} weitere Fehler`}
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              fontSize: '0.875rem',
                              color: 'error.main',
                              fontStyle: 'italic'
                            }
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {importResults ? 'Schließen' : 'Abbrechen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
